import {
	FC,
	KeyboardEvent as ReactKeyboardEvent,
	PointerEvent as ReactPointerEvent,
	useEffect,
	useRef,
	useState,
} from 'react';
import { Hsv, hexToHsv, hsvToHex, isValidHex } from '../../../colorUtils';
import styles from './PunchupColorPicker.module.css';

/* Keyboard step sizes for the HSV widgets. Saturation/brightness move in 5% steps;
 * hue moves in 5° steps. Keeps arrow-key adjustment usable without being twitchy. */
const SV_STEP = 0.05;
const HUE_STEP = 5;

interface ColorAreaProps {
	/** Current colour (hex). Drives the thumb positions; an invalid/empty value defaults to red. */
	value: string;
	/** Emits the new hex on every pointer move while dragging the square or the hue slider. */
	onChange: (hex: string) => void;
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/**
 * Saturation/brightness square + hue slider — the free-form half of the colour picker, beside the
 * preset grid. HSV is the natural model: the square sets saturation (x) and brightness (y) for the
 * current hue, the slider sets the hue. Local HSV state drives the thumbs so a drag is smooth; it
 * syncs from `value` only when the change came from outside (a preset click or hex entry), and
 * preserves the hue when the colour is greyscale (s === 0) so the square doesn't snap back to red.
 */
export const ColorArea: FC<ColorAreaProps> = ({ value, onChange }) => {
	const [hsv, setHsv] = useState<Hsv>(() => (isValidHex(value) ? hexToHsv(value) : { h: 0, s: 1, v: 1 }));
	const interacting = useRef(false);
	const squareRef = useRef<HTMLDivElement>(null);
	const hueRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Skip while dragging: our own onChange round-trips through `value` and would jitter the thumb.
		if (interacting.current || !isValidHex(value)) return;
		const next = hexToHsv(value);
		setHsv((prev) => ({ h: next.s === 0 ? prev.h : next.h, s: next.s, v: next.v }));
	}, [value]);

	const emit = (next: Hsv) => {
		setHsv(next);
		onChange(hsvToHex(next));
	};

	const moveSquare = (clientX: number, clientY: number) => {
		const rect = squareRef.current?.getBoundingClientRect();
		if (!rect || rect.width === 0 || rect.height === 0) return;
		emit({
			h: hsv.h,
			s: clamp01((clientX - rect.left) / rect.width),
			v: clamp01(1 - (clientY - rect.top) / rect.height),
		});
	};

	const moveHue = (clientX: number) => {
		const rect = hueRef.current?.getBoundingClientRect();
		if (!rect || rect.width === 0) return;
		emit({ ...hsv, h: clamp01((clientX - rect.left) / rect.width) * 360 });
	};

	const startDrag = (move: (x: number, y: number) => void) => (e: ReactPointerEvent<HTMLDivElement>) => {
		interacting.current = true;
		move(e.clientX, e.clientY);
		const onMove = (ev: PointerEvent) => move(ev.clientX, ev.clientY);
		const onUp = () => {
			interacting.current = false;
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', onUp);
		};
		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerup', onUp);
	};

	/* Keyboard control for the saturation/brightness square: Left/Right step saturation,
	 * Up/Down step brightness, Home/End jump to the saturation extremes. */
	const onSquareKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
		let { s, v } = hsv;
		switch (e.key) {
			case 'ArrowLeft':
				s = clamp01(s - SV_STEP);
				break;
			case 'ArrowRight':
				s = clamp01(s + SV_STEP);
				break;
			case 'ArrowUp':
				v = clamp01(v + SV_STEP);
				break;
			case 'ArrowDown':
				v = clamp01(v - SV_STEP);
				break;
			case 'Home':
				s = 0;
				break;
			case 'End':
				s = 1;
				break;
			default:
				return;
		}
		e.preventDefault();
		emit({ h: hsv.h, s, v });
	};

	/* Keyboard control for the hue slider: Left/Down decrease, Right/Up increase (wrapping
	 * around 360°), Home/End jump to the hue extremes. */
	const onHueKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
		let h = hsv.h;
		switch (e.key) {
			case 'ArrowLeft':
			case 'ArrowDown':
				h = (h - HUE_STEP + 360) % 360;
				break;
			case 'ArrowRight':
			case 'ArrowUp':
				h = (h + HUE_STEP) % 360;
				break;
			case 'Home':
				h = 0;
				break;
			case 'End':
				h = 360;
				break;
			default:
				return;
		}
		e.preventDefault();
		emit({ ...hsv, h });
	};

	return (
		<div className={styles.area}>
			<div
				ref={squareRef}
				className={styles.areaSquare}
				style={{ backgroundColor: hsvToHex({ h: hsv.h, s: 1, v: 1 }) }}
				onPointerDown={startDrag(moveSquare)}
				onKeyDown={onSquareKeyDown}
				tabIndex={0}
				role="slider"
				aria-label="Saturation and brightness"
				aria-valuetext={`${Math.round(hsv.s * 100)}% saturation, ${Math.round(hsv.v * 100)}% brightness`}
			>
				<span className={styles.areaThumb} style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }} />
			</div>
			<div
				ref={hueRef}
				className={styles.hueTrack}
				onPointerDown={startDrag(moveHue)}
				onKeyDown={onHueKeyDown}
				tabIndex={0}
				role="slider"
				aria-label="Hue"
				aria-valuenow={Math.round(hsv.h)}
				aria-valuemin={0}
				aria-valuemax={360}
			>
				<span className={styles.hueThumb} style={{ left: `${(hsv.h / 360) * 100}%` }} />
			</div>
		</div>
	);
};
